import { useCallback, useState } from 'react';

/**
 * Custom React hook for simple boolean state toggling.
 *
 * This hook manages a boolean state value and provides functions to toggle it
 * and set its value directly.
 * @param {boolean} initialState - The initial state value. Default is `false`.
 * @returns {Array} An array containing the following elements:
 *   - {boolean} state - The current state value.
 *   - {function} setToggle - Function to toggle the state value.
 *   - {function} setState - Function to set the state value directly.
 * @example
 * // Example usage of useSimpleToggle hook
 * const [isActive, toggleActive, setActive] = useSimpleToggle(true);
 *
 * // Toggling the state
 * toggleActive(); // Toggles `isActive` from `true` to `false` or vice versa.
 *
 * // Setting the state directly
 * setActive(false); // Sets `isActive` to `false`.
 */
export const useSimpleToggle = (initialState = false) => {
  const [state, setState] = useState(initialState);

  const setToggle = useCallback(() => {
    setState((prev) => !prev);
  }, []);

  return [state, setToggle, setState];
};
